import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { RootState } from "../../app/Store";
import {
  replaceTakesFromUnity,
  selectAllPropPresets,
  selectSelectedElement,
  selectTakes3D,
  setSelectedInteraction,
  setSelectedTake,
  updatePropPresetPrefabNames,
} from "../../app/slices/TrainingSlice";
import { ReconcileChange } from "../interaction_factory/InteractionFactory";
import { PropMetadata } from "../../dto/PropMetadata";
import {
  logPrefabNameChanges,
  reconcilePropPresets,
  reconcileTakes,
} from "../../helpers/PrefabNameReconciler";

/**
 * When Unity re-exports a prop (or situation — SituationMetadata extends
 * PropMetadata) with a changed prefab_name, update the prop presets and the
 * Attach/ShowUIProp interactions that snapshotted the old name. Dispatches
 * only when something actually changed, so a no-op sync produces no undo
 * step and no selection reset.
 */
export function syncPropPrefabNames(
  metadata: PropMetadata,
  dispatch: Dispatch<AnyAction>,
  getState: () => RootState
): void {
  if (!metadata.prefab_name) return; // never blank a prefab name

  const state = getState();
  const presets = selectAllPropPresets(state);
  const takes = selectTakes3D(state);
  const selectedElement = selectSelectedElement(state);

  const changes: ReconcileChange[] = [];
  const reconciledPresets = reconcilePropPresets(
    presets,
    { [metadata.id]: metadata.prefab_name },
    changes
  );
  if (reconciledPresets !== presets) {
    dispatch(
      updatePropPresetPrefabNames({
        propId: metadata.id,
        prefabName: metadata.prefab_name,
      })
    );
  }

  // sweep interactions only for this prop's preset instances
  const prefabNameByInstanceId: Record<string, string> = {};
  presets.forEach((preset) => {
    if (preset.propId === metadata.id)
      prefabNameByInstanceId[preset.id] = metadata.prefab_name;
  });
  const reconciledTakes = reconcileTakes(takes, {
    prefabNameByInstanceId,
    changes,
  });

  if (reconciledTakes !== takes) {
    const isTake = reconciledTakes.some(
      (take) => take.id === selectedElement?.id
    );
    dispatch(replaceTakesFromUnity(reconciledTakes));
    // replacing takes causes a refresh, so restore the selected element
    if (selectedElement) {
      if (isTake) dispatch(setSelectedTake(selectedElement.id));
      else dispatch(setSelectedInteraction(selectedElement.id));
    }
  }

  logPrefabNameChanges(changes, "sync");
}
